import React, { Ref, memo, useCallback, useMemo, useRef, useState } from 'react'
import { useDrag, useDrop } from 'react-dnd'

import { ShortListCustomFieldOption } from '@modules/shortlist/types'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { Box, Typography } from '@mui/material'

import {
  CategoryRowWrapper,
  CustomCategoryNameWrapper,
  CustomCategoryRowWrapper,
  EditCustomCategoryTextField,
} from './index.styles'

export interface CustomColumnCategoryRowProps {
  category: ShortListCustomFieldOption
  existingCategories?: ShortListCustomFieldOption[]
  onDelete?: () => void
  onUpdate?: (name: string) => void
  onOrderChange?: (categoryIndex: number, newCategoryOrder: number) => void
  index: number
  deletable: boolean
}

const CustomColumnCategoryRow = ({
  category,
  existingCategories,
  onDelete,
  onOrderChange,
  onUpdate,
  index,
  deletable,
}: CustomColumnCategoryRowProps) => {
  const dropRef = useRef(null)

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'custom-column-category',
      item: { index },
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [existingCategories, category],
  )

  const handleDropCallback = useCallback(
    (item: { index: number }) => {
      const draggedIndex = item.index
      const targetIndex = existingCategories?.indexOf(category) as number

      if (draggedIndex !== targetIndex) {
        onOrderChange?.(draggedIndex, targetIndex)
      }
    },
    [category, existingCategories, onOrderChange],
  )

  const [collectedProps, drop] = useDrop(
    () => ({
      accept: 'custom-column-category',
      drop: handleDropCallback,
      collect: monitor => {
        const isOver = monitor.isOver()
        let dropPosition = 'unknown'

        if (isOver) {
          const clientOffset = monitor.getClientOffset()
          if (clientOffset && dropRef.current) {
            // @ts-expect-error temp
            const rect = dropRef.current.getBoundingClientRect()
            const dropTargetHeight = rect.bottom - rect.top
            const dropTargetYMiddle = rect.top + dropTargetHeight / 2
            const dropY = clientOffset.y

            dropPosition = dropY < dropTargetYMiddle ? 'top' : 'bottom'
          }
        }

        return { isOver, dropPosition, draggedItem: monitor.getItem() }
      },
    }),
    [category, existingCategories],
  )

  const draggedItem = useMemo(
    () => existingCategories?.find((i, idx) => idx === collectedProps.draggedItem?.index),
    [collectedProps.draggedItem?.index, existingCategories],
  )

  const [isInEditMode, setIsInEditMode] = useState(false)

  drop(dropRef)

  return (
    <Box display="flex" flexDirection="column" ref={dropRef}>
      {collectedProps.dropPosition === 'top' && !!draggedItem && (
        <CustomCategoryRowWrapper $grey>
          <CategoryRowWrapper>
            <DragIndicatorIcon />
            <CustomCategoryNameWrapper>
              <Typography variant="body1">{draggedItem.value}</Typography>
            </CustomCategoryNameWrapper>
          </CategoryRowWrapper>
          <DeleteIcon sx={{ cursor: 'pointer' }} color="primary" />
        </CustomCategoryRowWrapper>
      )}
      <CustomCategoryRowWrapper $isDragged={isDragging}>
        <CategoryRowWrapper ref={drag as unknown as Ref<HTMLDivElement>}>
          <DragIndicatorIcon sx={{ cursor: 'grab' }} />
          {isInEditMode ? (
            <EditCustomCategoryTextField
              sx={{ width: '100%', maxHeight: '25px' }}
              autoFocus
              inputProps={{ maxLength: 20 }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                onUpdate?.(e.target?.value as string)
              }}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                onUpdate?.(e.target?.value as string)
              }}
              value={category.value}
            />
          ) : (
            <CustomCategoryNameWrapper onClick={() => setIsInEditMode?.(true)}>
              <Typography variant="body1">{category.value}</Typography>
            </CustomCategoryNameWrapper>
          )}
        </CategoryRowWrapper>
        {deletable && <DeleteIcon sx={{ cursor: 'pointer' }} onClick={() => onDelete?.()} color="primary" />}
      </CustomCategoryRowWrapper>
      {collectedProps.dropPosition === 'bottom' && !!draggedItem && (
        <CustomCategoryRowWrapper $grey>
          <CategoryRowWrapper>
            <DragIndicatorIcon />
            <CustomCategoryNameWrapper>
              <Typography variant="body1">{draggedItem.value}</Typography>
            </CustomCategoryNameWrapper>
          </CategoryRowWrapper>
          <DeleteIcon sx={{ cursor: 'pointer' }} color="primary" />
        </CustomCategoryRowWrapper>
      )}
    </Box>
  )
}

export default memo(CustomColumnCategoryRow)
